// src/components/AutoStaffAssignmentModal.js
// 🎓 教育重視版自動人員配置モーダル（完全改良版）

import React, { useState, useEffect, useRef } from "react";
import { runEnhancedStaffAssignment, runAutoStaffAssignment } from "../utils/staffAssignmentLogic";
import { EDUCATION_TYPES } from "../utils/educationUtils";
import dayjs from "dayjs";

const AutoStaffAssignmentModal = ({ isOpen, onClose, date, onApplyAssignments }) => {
  // 🆕 教育重視版用の拡張state
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedSurgery, setSelectedSurgery] = useState(null);
  const [assignments, setAssignments] = useState({});
  const [outpatientAssignments, setOutpatientAssignments] = useState({});
  const [manualAdjustments, setManualAdjustments] = useState([]);
  const [ruleValidation, setRuleValidation] = useState(null);
  const [aiProposal, setAiProposal] = useState(null);
  const [showAIPrompt, setShowAIPrompt] = useState(false);
  const [enhancedMode, setEnhancedMode] = useState(true);
  const [applyingAssignments, setApplyingAssignments] = useState(false);
  const hasRunRef = useRef(false);

  // 🆕 教育重視版タブ定義
  const tabs = [
    { id: "overview", label: "概要", icon: "📊" },
    { id: "assignments", label: "配置詳細", icon: "👥" },
    { id: "education", label: "教育配置", icon: "🎓" }, // メイン教育タブ
    { id: "outpatient", label: "外来配置", icon: "🏥" },
    { id: "timeline", label: "タイムライン", icon: "⏰" }, // 新規追加
    { id: "validation", label: "制約検証", icon: "🔍" },
    { id: "recommendations", label: "改善提案", icon: "💡" } // 新規追加
  ];

  // モーダルが開かれたときに自動配置を実行
  useEffect(() => {
    if (isOpen && !hasRunRef.current) {
      runAssignment();
      hasRunRef.current = true;
    }
    
    if (!isOpen) {
      hasRunRef.current = false;
    }
  }, [isOpen]);

  // 🆕 教育重視版自動配置を実行
  const runAssignment = async () => {
    setLoading(true);
    try {
      const formattedDate = dayjs(date).format("YYYY-MM-DD");
      
      let assignmentResult;
      
      if (enhancedMode) {
        try {
          assignmentResult = await runEnhancedStaffAssignment(formattedDate);
          console.log("🎓 教育重視版配置システム実行完了");
        } catch (error) {
          console.warn("教育重視版配置システムエラー、基本版にフォールバック:", error);
          assignmentResult = await runAutoStaffAssignment(formattedDate);
          assignmentResult.metadata = { 
            enhancedMode: false, 
            fallbackReason: "教育重視版でエラーが発生したため基本版で実行"
          };
        }
      } else {
        assignmentResult = await runAutoStaffAssignment(formattedDate);
        assignmentResult.metadata = { enhancedMode: false };
      }
      
      // 結果をstateに設定
      setResult(assignmentResult);
      setAssignments(assignmentResult.assignedSurgeries);
      setOutpatientAssignments(assignmentResult.outpatientAssignments || {});
      setRuleValidation(assignmentResult.enhancedValidation || null);
      setAiProposal(assignmentResult.aiProposal || null);
      setSelectedSurgery(null);
      setManualAdjustments([]);
      
    } catch (error) {
      console.error("自動配置エラー:", error);
      setResult({
        assignedSurgeries: {},
        outpatientAssignments: {},
        dailyStats: { 
          totalStaff: 0, 
          availableStaff: 0, 
          assignedStaff: 0, 
          unassignedStaff: [], 
          trainees: [],
          educationStats: {
            totalEducationOpportunities: 0,
            observationAssignments: 0,
            guidedPracticeAssignments: 0,
            supervisionAssignments: 0,
            assessmentAssignments: 0,
            additionalEducationAssignments: 0,
            unusedStaffCount: 0,
            utilizationRate: 0
          }
        },
        issues: [{ type: "system_error", message: "配置システムでエラーが発生しました" }],
        warnings: [],
        recommendations: [],
        metadata: { enhancedMode: false, error: error.message }
      });
    } finally {
      setLoading(false);
    }
  };

  // 🆕 手動調整を適用（教育配置対応版）
  const applyAdjustments = async () => {
    if (applyingAssignments) return;
    
    setApplyingAssignments(true);
    try {
      const updatedAssignments = { ...assignments };
      const updatedOutpatientAssignments = { ...outpatientAssignments };
      
      manualAdjustments.forEach(adjustment => {
        if (adjustment.type === "replace_staff") {
          const { surgeryId, role, staff } = adjustment;
          
          if (updatedAssignments[surgeryId]) {
            updatedAssignments[surgeryId][role] = staff;
          }
        }
      });
      
      // 教育履歴も含めて保存
      await onApplyAssignments(updatedAssignments, updatedOutpatientAssignments, result?.dailyStats?.educationStats);
      onClose();
    } catch (error) {
      console.error("配置適用エラー:", error);
      alert("配置の適用に失敗しました");
    } finally {
      setApplyingAssignments(false);
    }
  };

  // 🆕 教育タイプのラベル変換
  const getEducationTypeLabel = (type) => {
    const labels = {
      [EDUCATION_TYPES.FIRST_OBSERVATION]: "初回見学",
      [EDUCATION_TYPES.GUIDED_PRACTICE]: "指導下実践",
      [EDUCATION_TYPES.SUPERVISED_INDEPENDENCE]: "監督下独立",
      [EDUCATION_TYPES.FINAL_ASSESSMENT]: "最終評価",
      [EDUCATION_TYPES.ADDITIONAL_OBSERVATION]: "追加見学",
      [EDUCATION_TYPES.SURGICAL_ASSISTANCE]: "手術補助",
      [EDUCATION_TYPES.SKILL_DEVELOPMENT]: "スキル開発",
      [EDUCATION_TYPES.ADDITIONAL_TEACHING]: "追加指導"
    };
    return labels[type] || type;
  };

  // 🆕 教育統計の取得
  const getEducationStats = () => {
    return result?.dailyStats?.educationStats || {
      totalEducationOpportunities: 0,
      observationAssignments: 0,
      guidedPracticeAssignments: 0,
      supervisionAssignments: 0,
      assessmentAssignments: 0,
      additionalEducationAssignments: 0,
      unusedStaffCount: 0,
      utilizationRate: 0
    };
  };

  // 🆕 部屋別手術グループ化
  const groupSurgeriesByRoom = (surgeries) => {
    const grouped = {};
    
    Object.entries(surgeries).forEach(([surgeryId, assignment]) => {
      const room = assignment.surgery.room || "未定";
      if (!grouped[room]) {
        grouped[room] = [];
      }
      grouped[room].push({ surgeryId, ...assignment });
    });
    
    const sortedKeys = Object.keys(grouped).sort((a, b) => {
      const roomNumA = parseInt(a.replace(/\D/g, '')) || 9999;
      const roomNumB = parseInt(b.replace(/\D/g, '')) || 9999;
      return roomNumA - roomNumB;
    });
    
    const sortedGrouped = {};
    sortedKeys.forEach(key => {
      sortedGrouped[key] = grouped[key];
    });
    
    return sortedGrouped;
  };

  // 🆕 時間軸ベースの手術スケジュール生成
  const generateTimelineSchedule = (surgeries) => {
    const timeSlots = generateTimeSlots();
    const roomGroups = groupSurgeriesByRoom(surgeries);
    const availableStaffByTime = calculateAvailableStaffByTime(surgeries, timeSlots);
    
    return {
      rooms: roomGroups,
      timeSlots,
      availableStaffByTime
    };
  };

  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 8; hour < 18; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      slots.push(`${hour.toString().padStart(2, '0')}:30`);
    }
    return slots;
  };

  const calculateAvailableStaffByTime = (surgeries, timeSlots) => {
    const availableByTime = {};
    
    timeSlots.forEach(timeSlot => {
      const currentTime = dayjs(`2024-01-01 ${timeSlot}`);
      const busyStaff = new Set();
      
      Object.values(surgeries).forEach(assignment => {
        const surgeryStart = dayjs(`2024-01-01 ${assignment.surgery.start}`);
        const surgeryEnd = dayjs(`2024-01-01 ${assignment.surgery.end}`);
        
        if ((currentTime.isAfter(surgeryStart) || currentTime.isSame(surgeryStart)) && 
            currentTime.isBefore(surgeryEnd)) {
          if (assignment.scrub) busyStaff.add(assignment.scrub.employeeId);
          if (assignment.circulating) busyStaff.add(assignment.circulating.employeeId);
          
          // 教育配置のスタッフも考慮
          if (assignment.educationAssignment) {
            if (assignment.educationAssignment.instructor) busyStaff.add(assignment.educationAssignment.instructor.employeeId);
            if (assignment.educationAssignment.observer) busyStaff.add(assignment.educationAssignment.observer.employeeId);
            if (assignment.educationAssignment.supervisor) busyStaff.add(assignment.educationAssignment.supervisor.employeeId);
            if (assignment.educationAssignment.evaluator) busyStaff.add(assignment.educationAssignment.evaluator.employeeId);
          }
          
          if (assignment.additionalEducation) {
            if (assignment.additionalEducation.assistant) busyStaff.add(assignment.additionalEducation.assistant.employeeId);
            if (assignment.additionalEducation.developer) busyStaff.add(assignment.additionalEducation.developer.employeeId);
            if (assignment.additionalEducation.teacher) busyStaff.add(assignment.additionalEducation.teacher.employeeId);
          }
        }
      });
      
      if (result.outpatientAssignments) {
        Object.values(result.outpatientAssignments).flat().forEach(staff => {
          if (staff.employeeId) busyStaff.add(staff.employeeId);
        });
      }
      
      const availableStaff = (result.dailyStats.unassignedStaff || []).filter(staff => 
        !busyStaff.has(staff.employeeId)
      );
      
      availableByTime[timeSlot] = availableStaff;
    });
    
    return availableByTime;
  };

  const calculateSurgeryWidth = (surgery) => {
    if (!surgery.start || !surgery.end) return 2;
    
    const start = dayjs(`2024-01-01 ${surgery.start}`);
    const end = dayjs(`2024-01-01 ${surgery.end}`);
    const duration = end.diff(start, 'minute');
    
    return Math.max(2, Math.floor(duration / 30));
  };

  const calculateSurgeryPosition = (surgery, timeSlots) => {
    if (!surgery.start) return 0;
    
    const startTime = dayjs(`2024-01-01 ${surgery.start}`);
    const baseTime = dayjs(`2024-01-01 08:00`);
    const offsetMinutes = startTime.diff(baseTime, 'minute');
    
    return Math.floor(offsetMinutes / 30);
  };

  // スタイル定義
  const styles = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 10002
    },
    modal: {
      backgroundColor: "white",
      borderRadius: "8px",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
      width: "95%",
      maxWidth: "1400px",
      maxHeight: "95vh",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column"
    },
    header: {
      padding: "1rem 1.5rem",
      borderBottom: "1px solid #e2e8f0",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: enhancedMode ? "#4F46E5" : "#6B7280",
      color: "white"
    },
    title: {
      fontSize: "1.25rem",
      fontWeight: "bold",
      color: "white"
    },
    closeButton: {
      background: "none",
      border: "none",
      fontSize: "1.5rem",
      cursor: "pointer",
      color: "white"
    },
    content: {
      padding: "1.5rem",
      overflowY: "auto",
      flex: 1
    },
    footer: {
      padding: "1rem 1.5rem",
      borderTop: "1px solid #e2e8f0",
      display: "flex",
      justifyContent: "space-between",
      gap: "0.5rem",
      backgroundColor: "#f8fafc"
    },
    tabContainer: {
      display: "flex",
      borderBottom: "1px solid #e2e8f0",
      marginBottom: "1rem",
      backgroundColor: "#f8fafc"
    },
    tab: {
      padding: "0.75rem 1.5rem",
      cursor: "pointer",
      borderBottom: "3px solid transparent",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
      fontSize: "0.9rem",
      fontWeight: "500"
    },
    activeTab: {
      borderBottomColor: "#4F46E5",
      color: "#4F46E5",
      fontWeight: "bold",
      backgroundColor: "white"
    },
    loadingContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "3rem",
      minHeight: "400px"
    },
    spinner: {
      borderRadius: "50%",
      width: "3rem",
      height: "3rem",
      margin: "1rem",
      borderTop: "3px solid #4F46E5",
      borderRight: "3px solid transparent",
      animation: "spin 1s linear infinite"
    },
    statsCard: {
      backgroundColor: "#f7fafc",
      borderRadius: "8px",
      padding: "1rem",
      marginBottom: "1rem",
      border: "1px solid #e2e8f0"
    },
    statGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
      gap: "0.75rem",
      marginTop: "0.5rem"
    },
    statItem: {
      backgroundColor: "white",
      padding: "0.75rem",
      borderRadius: "6px",
      textAlign: "center",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
      border: "1px solid #e2e8f0"
    },
    statValue: {
      fontSize: "1.5rem",
      fontWeight: "bold",
      color: "#4F46E5"
    },
    statLabel: {
      fontSize: "0.8rem",
      color: "#718096",
      marginTop: "0.25rem"
    },
    // 🆕 教育配置専用スタイル
    educationCard: {
      backgroundColor: "#f0f9ff",
      border: "1px solid #93c5fd",
      borderRadius: "8px",
      padding: "1rem",
      marginBottom: "1rem"
    },
    educationGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
      gap: "1rem",
      marginTop: "1rem"
    },
    educationItem: {
      backgroundColor: "white",
      padding: "1rem",
      borderRadius: "6px",
      border: "1px solid #cbd5e1"
    },
    educationHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: "0.75rem"
    },
    educationBadge: {
      padding: "0.25rem 0.5rem",
      borderRadius: "12px",
      fontSize: "0.75rem",
      fontWeight: "500"
    },
    observationBadge: {
      backgroundColor: "#dbeafe",
      color: "#1e40af"
    },
    practiceBadge: {
      backgroundColor: "#dcfce7",
      color: "#166534"
    },
    supervisionBadge: {
      backgroundColor: "#fef3c7",
      color: "#92400e"
    },
    assessmentBadge: {
      backgroundColor: "#fce7f3",
      color: "#be185d"
    },
    button: {
      padding: "0.75rem 1.5rem",
      borderRadius: "0.5rem",
      fontWeight: "500",
      cursor: "pointer",
      fontSize: "0.9rem",
      border: "none",
      transition: "all 0.2s"
    },
    primaryButton: {
      backgroundColor: "#4F46E5",
      color: "white"
    },
    secondaryButton: {
      backgroundColor: "#e2e8f0",
      color: "#4a5568"
    },
    successButton: {
      backgroundColor: "#10b981",
      color: "white"
    },
    modeToggle: {
      padding: "0.5rem 1rem",
      borderRadius: "0.375rem",
      fontSize: "0.875rem",
      fontWeight: "500",
      cursor: "pointer",
      border: "2px solid #4F46E5",
      backgroundColor: enhancedMode ? "#4F46E5" : "white",
      color: enhancedMode ? "white" : "#4F46E5"
    }
  };

  // キーフレームアニメーション
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (!isOpen) {
    return null;
  }

  // ロード中表示
  if (loading) {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <div style={styles.header}>
            <h2 style={styles.title}>
              🎓 教育重視版自動人員配置
            </h2>
            <button style={styles.closeButton} onClick={onClose}>×</button>
          </div>
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}></div>
            <p style={{ fontSize: "1.1rem", marginBottom: "0.5rem" }}>教育配置の計算中...</p>
            <p style={{ fontSize: "0.9rem", color: "#6b7280" }}>
              個別教育ニーズ分析・最適配置・教育履歴更新を実行しています
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <div style={styles.header}>
            <h2 style={styles.title}>自動人員配置</h2>
            <button style={styles.closeButton} onClick={onClose}>×</button>
          </div>
          <div style={styles.content}>
            <p>配置計算に失敗しました。</p>
            <button 
              onClick={runAssignment}
              style={{...styles.button, ...styles.primaryButton}}
            >
              再試行
            </button>
          </div>
        </div>
      </div>
    );
  }

  const educationStats = getEducationStats();

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>
            🎓 教育重視版 {dayjs(date).format("YYYY年MM月DD日")}の自動人員配置
          </h2>
          <button style={styles.closeButton} onClick={onClose}>×</button>
        </div>
        
        {/* タブ */}
        <div style={styles.tabContainer}>
          {tabs.map(tab => (
            <div 
              key={tab.id}
              style={{
                ...styles.tab, 
                ...(activeTab === tab.id ? styles.activeTab : {})
              }}
              onClick={() => setActiveTab(tab.id)}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </div>
          ))}
        </div>
        
        <div style={styles.content}>
          {/* 📊 概要タブ */}
          {activeTab === "overview" && (
            <div>
              {/* システム情報 */}
              <div style={styles.statsCard}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                  <h3 style={{ margin: 0 }}>システム情報</h3>
                  <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
                    <span style={{ 
                      color: result.metadata?.enhancedMode !== false ? "#10b981" : "#6b7280",
                      fontWeight: "bold",
                      fontSize: "0.9rem"
                    }}>
                      🎓 教育重視版動作中
                    </span>
                    <button
                      onClick={() => {
                        setEnhancedMode(!enhancedMode);
                        runAssignment();
                      }}
                      style={styles.modeToggle}
                    >
                      {enhancedMode ? "基本版に切替" : "教育版に切替"}
                    </button>
                  </div>
                </div>
                
                {result.metadata?.fallbackReason && (
                  <div style={{ 
                    padding: "0.75rem", 
                    backgroundColor: "#fef3c7", 
                    borderRadius: "6px", 
                    marginBottom: "1rem",
                    border: "1px solid #f59e0b"
                  }}>
                    <span style={{ color: "#92400e", fontWeight: "500" }}>
                      ⚠️ {result.metadata.fallbackReason}
                    </span>
                  </div>
                )}
              </div>

              {/* 人員配置サマリー */}
              <div style={styles.statsCard}>
                <h3>人員配置サマリー</h3>
                <div style={styles.statGrid}>
                  <div style={styles.statItem}>
                    <div style={styles.statValue}>{result.dailyStats.totalStaff}</div>
                    <div style={styles.statLabel}>日勤者総数</div>
                  </div>
                  <div style={styles.statItem}>
                    <div style={styles.statValue}>{result.dailyStats.assignedStaff}</div>
                    <div style={styles.statLabel}>配置済み</div>
                  </div>
                  <div style={styles.statItem}>
                    <div style={styles.statValue}>{Object.keys(result.assignedSurgeries).length}</div>
                    <div style={styles.statLabel}>手術数</div>
                  </div>
                  <div style={styles.statItem}>
                    <div style={styles.statValue}>{result.dailyStats.outpatientStaff || 0}</div>
                    <div style={styles.statLabel}>外来配置</div>
                  </div>
                  <div style={styles.statItem}>
                    <div style={styles.statValue}>{result.dailyStats.availableStaff}</div>
                    <div style={styles.statLabel}>未配置</div>
                  </div>
                  <div style={styles.statItem}>
                    <div style={styles.statValue}>{educationStats.utilizationRate.toFixed(1)}%</div>
                    <div style={styles.statLabel}>活用率</div>
                  </div>
                </div>
              </div>
              
              {/* 🆕 教育統計サマリー */}
              <div style={styles.statsCard}>
                <h3 style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  🎓 教育配置統計
                </h3>
                <div style={styles.statGrid}>
                  <div style={styles.statItem}>
                    <div style={styles.statValue}>{educationStats.totalEducationOpportunities}</div>
                    <div style={styles.statLabel}>教育機会総数</div>
                  </div>
                  <div style={styles.statItem}>
                    <div style={styles.statValue}>{educationStats.observationAssignments}</div>
                    <div style={styles.statLabel}>見学配置</div>
                  </div>
                  <div style={styles.statItem}>
                    <div style={styles.statValue}>{educationStats.guidedPracticeAssignments}</div>
                    <div style={styles.statLabel}>実践指導</div>
                  </div>
                  <div style={styles.statItem}>
                    <div style={styles.statValue}>{educationStats.supervisionAssignments}</div>
                    <div style={styles.statLabel}>監督下独立</div>
                  </div>
                  <div style={styles.statItem}>
                    <div style={styles.statValue}>{educationStats.assessmentAssignments}</div>
                    <div style={styles.statLabel}>最終評価</div>
                  </div>
                  <div style={styles.statItem}>
                    <div style={styles.statValue}>{educationStats.additionalEducationAssignments}</div>
                    <div style={styles.statLabel}>追加教育</div>
                  </div>
                </div>
                
                {/* 活用率インジケーター */}
                <div style={{ marginTop: "1rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                    <span style={{ fontSize: "0.9rem", fontWeight: "500" }}>教育活用率</span>
                    <span style={{ fontSize: "0.9rem", fontWeight: "bold" }}>
                      {educationStats.utilizationRate.toFixed(1)}%
                    </span>
                  </div>
                  <div style={{ width: "100%", backgroundColor: "#e5e7eb", borderRadius: "9999px", height: "0.5rem" }}>
                    <div 
                      style={{ 
                        backgroundColor: educationStats.utilizationRate >= 80 ? "#10b981" : educationStats.utilizationRate >= 60 ? "#f59e0b" : "#ef4444",
                        height: "0.5rem", 
                        borderRadius: "9999px", 
                        transition: "all 0.3s",
                        width: `${Math.min(100, educationStats.utilizationRate)}%` 
                      }}
                    ></div>
                  </div>
                </div>
                
                {/* 未配置人員の警告 */}
                {educationStats.unusedStaffCount > 0 && (
                  <div style={{ 
                    marginTop: "1rem",
                    padding: "0.75rem", 
                    backgroundColor: "#fef3c7", 
                    borderRadius: "6px",
                    border: "1px solid #f59e0b"
                  }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span style={{ color: "#92400e", marginRight: "0.5rem" }}>⚠️</span>
                      <span style={{ fontSize: "0.9rem", color: "#92400e" }}>
                        {educationStats.unusedStaffCount}人のスタッフが教育配置されていません
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* 👥 配置詳細タブ */}
          {activeTab === "assignments" && (
            <div>
              <h3>配置詳細</h3>
              {Object.entries(result.assignedSurgeries).sort((a, b) => {
                const roomA = a[1].surgery.room || '';
                const roomB = b[1].surgery.room || '';
                const roomNumA = parseInt(roomA.replace(/\D/g, '')) || 9999;
                const roomNumB = parseInt(roomB.replace(/\D/g, '')) || 9999;
                return roomNumA - roomNumB;
              }).map(([surgeryId, data]) => (
                <div key={surgeryId} style={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  marginBottom: "1rem",
                  overflow: "hidden",
                  border: "1px solid #e2e8f0"
                }}>
                  <div style={{
                    padding: "1rem",
                    backgroundColor: "#f7fafc",
                    borderBottom: "1px solid #e2e8f0",
                    fontWeight: "bold"
                  }}>
                    {data.surgery.room || "部屋未定"} - {data.surgery.procedure || "未定"} ({data.surgery.start || "??:??"} - {data.surgery.end || "??:??"})
                  </div>
                  <div style={{ padding: "1.5rem" }}>
                    <div style={{ marginBottom: "0.75rem" }}>
                      <strong>執刀医:</strong> {data.surgery.surgeon || "未定"}
                    </div>
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                      gap: "1.5rem"
                    }}>
                      <div>
                        <div style={{ marginBottom: "0.5rem" }}>器械出し</div>
                        {data.scrub ? (
                          <div style={{
                            backgroundColor: "#f7fafc",
                            padding: "1rem",
                            borderRadius: "6px",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.75rem",
                            border: "1px solid #e2e8f0"
                          }}>
                            <div style={{
                              width: "2.5rem",
                              height: "2.5rem",
                              borderRadius: "50%",
                              backgroundColor: "#e2e8f0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "1rem",
                              fontWeight: "bold",
                              color: "#4a5568"
                            }}>
                              {data.scrub.lastName ? data.scrub.lastName.charAt(0) : "?"}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: "bold", fontSize: "1rem" }}>
                                {data.scrub.lastName} {data.scrub.firstName}
                              </div>
                              <div style={{ fontSize: "0.875rem", color: "#718096", marginTop: "0.25rem" }}>
                                {data.scrub.experienceYears}年目 (スキル: {data.scrub.skillScore?.toFixed(1) || "不明"})
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div style={{
                            padding: "0.75rem",
                            backgroundColor: "#fee2e2",
                            borderRadius: "6px",
                            color: "#b91c1c"
                          }}>
                            未割当
                          </div>
                        )}
                      </div>
                      <div>
                        <div style={{ marginBottom: "0.5rem" }}>外回り</div>
                        {data.circulating ? (
                          <div style={{
                            backgroundColor: "#f7fafc",
                            padding: "1rem",
                            borderRadius: "6px",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.75rem",
                            border: "1px solid #e2e8f0"
                          }}>
                            <div style={{
                              width: "2.5rem",
                              height: "2.5rem",
                              borderRadius: "50%",
                              backgroundColor: "#e2e8f0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "1rem",
                              fontWeight: "bold",
                              color: "#4a5568"
                            }}>
                              {data.circulating.lastName ? data.circulating.lastName.charAt(0) : "?"}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: "bold", fontSize: "1rem" }}>
                                {data.circulating.lastName} {data.circulating.firstName}
                              </div>
                              <div style={{ fontSize: "0.875rem", color: "#718096", marginTop: "0.25rem" }}>
                                {data.circulating.experienceYears}年目 (スキル: {data.circulating.skillScore?.toFixed(1) || "不明"})
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div style={{
                            padding: "0.75rem",
                            backgroundColor: "#fee2e2",
                            borderRadius: "6px",
                            color: "#b91c1c"
                          }}>
                            未割当
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* 🎓 教育配置タブ（メイン） */}
          {activeTab === "education" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
                <h3>教育配置詳細</h3>
                <div style={{ display: "flex", gap: "1rem", fontSize: "0.9rem" }}>
                  <span style={{ color: "#10b981", fontWeight: "500" }}>
                    ✅ 配置済み: {educationStats.totalEducationOpportunities}件
                  </span>
                  <span style={{ color: "#f59e0b", fontWeight: "500" }}>
                    ➕ 追加配置: {educationStats.additionalEducationAssignments}件
                  </span>
                </div>
              </div>
              
              {educationStats.totalEducationOpportunities === 0 && educationStats.additionalEducationAssignments === 0 ? (
                <div style={{
                  textAlign: "center",
                  padding: "3rem",
                  backgroundColor: "#f9fafb",
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb"
                }}>
                  <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🎓</div>
                  <h4 style={{ color: "#6b7280", marginBottom: "0.5rem" }}>教育配置なし</h4>
                  <p style={{ color: "#9ca3af", fontSize: "0.9rem" }}>
                    教育機会が作成されませんでした。<br/>
                    スタッフの経験レベルや手術の難易度を確認してください。
                  </p>
                </div>
              ) : (
                <div style={styles.educationGrid}>
                  {Object.entries(result.assignedSurgeries).map(([surgeryId, assignment]) => {
                    const hasEducation = assignment.educationAssignment || assignment.additionalEducation;
                    if (!hasEducation) return null;
                    
                    return (
                      <div key={surgeryId} style={styles.educationItem}>
                        <div style={styles.educationHeader}>
                          <div>
                            <h4 style={{ margin: "0 0 0.25rem 0", fontSize: "1rem" }}>
                              {assignment.surgery?.procedure}
                            </h4>
                            <p style={{ margin: 0, fontSize: "0.85rem", color: "#6b7280" }}>
                              {assignment.surgery?.room} | {assignment.surgery?.start} - {assignment.surgery?.end}
                            </p>
                          </div>
                          {assignment.educationAssignment && (
                            <span style={{
                              ...styles.educationBadge,
                              ...getBadgeStyle(assignment.educationAssignment.type)
                            }}>
                              {getEducationTypeLabel(assignment.educationAssignment.type)}
                            </span>
                          )}
                        </div>
                        
                        {/* 基本配置 */}
                        <div style={{ 
                          display: "grid", 
                          gridTemplateColumns: "1fr 1fr", 
                          gap: "0.75rem", 
                          marginBottom: "1rem",
                          padding: "0.75rem",
                          backgroundColor: "#f8fafc",
                          borderRadius: "4px"
                        }}>
                          <div style={{ fontSize: "0.85rem" }}>
                            <span style={{ fontWeight: "500" }}>器械出し: </span>
                            <span>{assignment.scrub?.lastName} {assignment.scrub?.firstName}</span>
                          </div>
                          <div style={{ fontSize: "0.85rem" }}>
                            <span style={{ fontWeight: "500" }}>外回り: </span>
                            <span>{assignment.circulating?.lastName} {assignment.circulating?.firstName}</span>
                          </div>
                        </div>
                        
                        {/* 主要教育配置 */}
                        {assignment.educationAssignment && (
                          <div style={{
                            padding: "0.75rem",
                            backgroundColor: "#f0f9ff",
                            borderRadius: "6px",
                            border: "1px solid #93c5fd",
                            marginBottom: "0.75rem"
                          }}>
                            <h5 style={{ margin: "0 0 0.5rem 0", color: "#1e40af", fontSize: "0.9rem" }}>
                              🎯 {getEducationTypeLabel(assignment.educationAssignment.type)}
                            </h5>
                            <div style={{ fontSize: "0.8rem", lineHeight: "1.4" }}>
                              {assignment.educationAssignment.instructor && (
                                <p style={{ margin: "0 0 0.25rem 0" }}>
                                  <span style={{ fontWeight: "500" }}>指導者:</span> {assignment.educationAssignment.instructor.lastName} {assignment.educationAssignment.instructor.firstName}
                                </p>
                              )}
                              {assignment.educationAssignment.observer && (
                                <p style={{ margin: "0 0 0.25rem 0" }}>
                                  <span style={{ fontWeight: "500" }}>見学者:</span> {assignment.educationAssignment.observer.lastName} {assignment.educationAssignment.observer.firstName}
                                </p>
                              )}
                              {assignment.educationAssignment.supervisor && (
                                <p style={{ margin: "0 0 0.25rem 0" }}>
                                  <span style={{ fontWeight: "500" }}>監督者:</span> {assignment.educationAssignment.supervisor.lastName} {assignment.educationAssignment.supervisor.firstName}
                                </p>
                              )}
                              {assignment.educationAssignment.evaluator && (
                                <p style={{ margin: "0 0 0.25rem 0" }}>
                                  <span style={{ fontWeight: "500" }}>評価者:</span> {assignment.educationAssignment.evaluator.lastName} {assignment.educationAssignment.evaluator.firstName}
                                </p>
                              )}
                              <p style={{ margin: "0.5rem 0 0 0", fontStyle: "italic", color: "#4b5563" }}>
                                目標: {assignment.educationAssignment.educationGoal}
                              </p>
                            </div>
                          </div>
                        )}
                        
                        {/* 追加教育配置 */}
                        {assignment.additionalEducation && (
                          <div style={{
                            padding: "0.75rem",
                            backgroundColor: "#f0fdf4",
                            borderRadius: "6px",
                            border: "1px solid #86efac"
                          }}>
                            <h5 style={{ margin: "0 0 0.5rem 0", color: "#166534", fontSize: "0.9rem" }}>
                              ➕ {getEducationTypeLabel(assignment.additionalEducation.type)}
                            </h5>
                            <div style={{ fontSize: "0.8rem", lineHeight: "1.4" }}>
                              {assignment.additionalEducation.assistant && (
                                <p style={{ margin: "0 0 0.25rem 0" }}>
                                  <span style={{ fontWeight: "500" }}>補助者:</span> {assignment.additionalEducation.assistant.lastName} {assignment.additionalEducation.assistant.firstName}
                                </p>
                              )}
                              {assignment.additionalEducation.developer && (
                                <p style={{ margin: "0 0 0.25rem 0" }}>
                                  <span style={{ fontWeight: "500" }}>開発者:</span> {assignment.additionalEducation.developer.lastName} {assignment.additionalEducation.developer.firstName}
                                </p>
                              )}
                              {assignment.additionalEducation.teacher && (
                                <p style={{ margin: "0 0 0.25rem 0" }}>
                                  <span style={{ fontWeight: "500" }}>指導補助:</span> {assignment.additionalEducation.teacher.lastName} {assignment.additionalEducation.teacher.firstName}
                                </p>
                              )}
                              <p style={{ margin: "0.5rem 0 0 0", fontStyle: "italic", color: "#4b5563" }}>
                                目的: {assignment.additionalEducation.purpose}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          
          {/* 🏥 外来配置タブ */}
          {activeTab === "outpatient" && (
            <div>
              <h3>外来人員配置</h3>
              
              {result.outpatientAssignments && Object.keys(result.outpatientAssignments).length > 0 ? (
                <div style={{
                  backgroundColor: "#f0f9ff",
                  border: "1px solid #93c5fd",
                  borderRadius: "8px",
                  padding: "1.5rem",
                  marginBottom: "1rem"
                }}>
                  <h4 style={{ margin: "0 0 1rem 0", color: "#1e40af" }}>配置状況</h4>
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "1rem"
                  }}>
                    {Object.entries(result.outpatientAssignments).map(([role, staffList]) => (
                      <div key={role} style={{
                        backgroundColor: "white",
                        padding: "1rem",
                        borderRadius: "6px",
                        border: "1px solid #cbd5e1"
                      }}>
                        <div style={{
                          fontWeight: "bold",
                          marginBottom: "0.75rem",
                          color: "#1e40af"
                        }}>
                          {role === "leader" ? "リーダー" : role === "member" ? "メンバー" : role}
                          （{Array.isArray(staffList) ? staffList.length : 0}名）
                        </div>
                        <div style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.5rem"
                        }}>
                          {Array.isArray(staffList) && staffList.map((staff, index) => (
                            <div key={index} style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              padding: "0.5rem",
                              backgroundColor: "#f8fafc",
                              borderRadius: "4px",
                              border: "1px solid #e2e8f0"
                            }}>
                              <div style={{
                                width: "2rem",
                                height: "2rem",
                                borderRadius: "50%",
                                backgroundColor: "#e2e8f0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "0.875rem",
                                fontWeight: "bold",
                                color: "#4a5568"
                              }}>
                                {staff.lastName ? staff.lastName.charAt(0) : "?"}
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: "500" }}>
                                  {staff.lastName} {staff.firstName}
                                </div>
                                <div style={{ fontSize: "0.8rem", color: "#6b7280" }}>
                                  {staff.experienceYears || 0}年目 | スキル: {staff.skillScore?.toFixed(1) || "不明"}
                                </div>
                              </div>
                            </div>
                          ))}
                          {(!Array.isArray(staffList) || staffList.length === 0) && (
                            <div style={{ 
                              textAlign: "center", 
                              color: "#9ca3af", 
                              fontSize: "0.9rem",
                              padding: "1rem" 
                            }}>
                              配置なし
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{
                  backgroundColor: "#fef3c7",
                  border: "1px solid #f6e05e",
                  borderRadius: "8px",
                  padding: "1.5rem"
                }}>
                  <p>🏥 外来配置情報がありません。</p>
                  <p>教育重視版モードで実行すると外来人員配置が自動実行されます。</p>
                </div>
              )}
            </div>
          )}
          
          {/* ⏰ タイムラインタブ */}
          {activeTab === "timeline" && (
            <div>
              <h3 style={{ marginBottom: "0.75rem" }}>配置タイムライン</h3>
              
              {(() => {
                const timelineData = generateTimelineSchedule(result.assignedSurgeries);
                
                return (
                  <div>
                    {/* タイムライン表示 */}
                    <div style={{
                      backgroundColor: "white",
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                      overflow: "hidden",
                      marginBottom: "1rem"
                    }}>
                      {/* ヘッダー */}
                      <div style={{
                        display: "grid",
                        gridTemplateColumns: "100px 1fr",
                        backgroundColor: "#f8fafc",
                        borderBottom: "1px solid #e2e8f0"
                      }}>
                        <div style={{
                          padding: "1rem 0.75rem",
                          backgroundColor: "#4F46E5",
                          color: "white",
                          fontWeight: "bold",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.9rem"
                        }}>部屋</div>
                        <div style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(20, 1fr)",
                          gap: "1px",
                          backgroundColor: "#e2e8f0"
                        }}>
                          {timelineData.timeSlots.map(timeSlot => (
                            <div key={timeSlot} style={{
                              padding: "0.5rem 0.25rem",
                              textAlign: "center",
                              fontSize: "0.75rem",
                              backgroundColor: "#f8fafc",
                              borderRight: "1px solid #e2e8f0"
                            }}>
                              {timeSlot}
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* 各部屋の行 */}
                      {Object.entries(timelineData.rooms).map(([room, surgeries]) => (
                        <div key={room} style={{
                          display: "grid",
                          gridTemplateColumns: "100px 1fr",
                          borderBottom: "1px solid #f1f5f9",
                          minHeight: "80px",
                          alignItems: "stretch"
                        }}>
                          <div style={{
                            padding: "1rem 0.75rem",
                            backgroundColor: "#f8fafc",
                            borderRight: "1px solid #e2e8f0",
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.9rem"
                          }}>{room}</div>
                          <div style={{
                            position: "relative",
                            display: "grid",
                            gridTemplateColumns: "repeat(20, 1fr)",
                            gap: "1px",
                            backgroundColor: "#f9fafb",
                            padding: "0.5rem 0"
                          }}>
                            {surgeries.map((surgery, index) => {
                              const position = calculateSurgeryPosition(surgery.surgery, timelineData.timeSlots);
                              const width = calculateSurgeryWidth(surgery.surgery);
                              const leftPercent = (position / 20) * 100;
                              const widthPercent = (width / 20) * 100;
                              
                              return (
                                <div
                                  key={surgery.surgeryId}
                                  style={{
                                    position: "absolute",
                                    backgroundColor: "#dbeafe",
                                    border: "1px solid #93c5fd",
                                    borderRadius: "4px",
                                    padding: "0.25rem",
                                    fontSize: "0.75rem",
                                    top: "0.5rem",
                                    bottom: "0.5rem",
                                    left: `${leftPercent}%`,
                                    width: `${Math.min(widthPercent, 100 - leftPercent)}%`,
                                    zIndex: 1,
                                    overflow: "hidden"
                                  }}
                                >
                                  <div style={{
                                    fontWeight: "bold",
                                    fontSize: "0.7rem",
                                    marginBottom: "0.25rem",
                                    color: "#1e40af",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis"
                                  }}>
                                    {surgery.surgery.procedure || "未定"}
                                  </div>
                                  <div style={{ fontSize: "0.6rem", color: "#6b7280", marginBottom: "0.25rem" }}>
                                    {surgery.surgery.start} - {surgery.surgery.end}
                                  </div>
                                  
                                  <div style={{ display: "flex", gap: "0.25rem", fontSize: "0.65rem" }}>
                                    <div style={{
                                      backgroundColor: "#fef3c7",
                                      padding: "0.125rem 0.25rem",
                                      borderRadius: "2px",
                                      flex: 1,
                                      textAlign: "center",
                                      border: "1px solid #f59e0b"
                                    }}>
                                      器:{surgery.scrub ? 
                                        `${surgery.scrub.lastName}${surgery.scrub.firstName}` : 
                                        "未割当"
                                      }
                                    </div>
                                    
                                    <div style={{
                                      backgroundColor: "#d1fae5",
                                      padding: "0.125rem 0.25rem",
                                      borderRadius: "2px",
                                      flex: 1,
                                      textAlign: "center",
                                      border: "1px solid #10b981"
                                    }}>
                                      外:{surgery.circulating ? 
                                        `${surgery.circulating.lastName}${surgery.circulating.firstName}` : 
                                        "未割当"
                                      }
                                    </div>
                                  </div>
                                  
                                  {/* 教育配置インジケーター */}
                                  {surgery.educationAssignment && (
                                    <div style={{
                                      marginTop: "0.25rem",
                                      fontSize: "0.6rem",
                                      backgroundColor: "#3B82F6",
                                      color: "white",
                                      padding: "0.125rem 0.25rem",
                                      borderRadius: "2px",
                                      textAlign: "center"
                                    }}>
                                      🎓 教育
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
          
          {/* 🔍 制約検証タブ */}
          {activeTab === "validation" && (
            <div>
              <h3>制約検証結果</h3>
              <div style={{
                textAlign: "center",
                padding: "3rem",
                backgroundColor: "#f9fafb",
                borderRadius: "8px"
              }}>
                <p>🔍 制約検証機能は今後実装予定です。</p>
              </div>
            </div>
          )}
          
          {/* 💡 改善提案タブ */}
          {activeTab === "recommendations" && (
            <div>
              <h3>改善提案</h3>
              
              {result.recommendations && result.recommendations.length > 0 ? (
                <div style={{ space: "1rem" }}>
                  {result.recommendations.map((recommendation, index) => (
                    <div key={index} style={{
                      backgroundColor: "#f0f9ff",
                      border: "1px solid #93c5fd",
                      borderRadius: "8px",
                      padding: "1rem",
                      marginBottom: "1rem"
                    }}>
                      <h4 style={{ margin: "0 0 0.5rem 0", color: "#1e40af" }}>
                        💡 {recommendation.type === "improve_education_utilization" ? "教育活用改善" :
                            recommendation.type === "create_education_opportunities" ? "教育機会創出" :
                            recommendation.type === "increase_staff" ? "人員増強" :
                            recommendation.type === "optimize_schedule" ? "スケジュール最適化" : "改善提案"}
                      </h4>
                      <p style={{ margin: 0, color: "#4b5563" }}>
                        {recommendation.message}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  textAlign: "center",
                  padding: "3rem",
                  backgroundColor: "#f0fdf4",
                  borderRadius: "8px",
                  border: "1px solid #86efac"
                }}>
                  <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>✅</div>
                  <h4 style={{ color: "#166534", marginBottom: "0.5rem" }}>改善提案なし</h4>
                  <p style={{ color: "#22c55e", fontSize: "0.9rem" }}>
                    現在の配置に大きな問題は見つかりませんでした
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div style={styles.footer}>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button 
              style={{...styles.button, ...styles.secondaryButton}}
              onClick={runAssignment}
            >
              🔄 再計算
            </button>
          </div>
          
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button 
              style={{...styles.button, ...styles.secondaryButton}}
              onClick={onClose}
            >
              キャンセル
            </button>
            <button 
              style={{
                ...styles.button, 
                ...styles.primaryButton,
                opacity: applyingAssignments ? 0.6 : 1,
                cursor: applyingAssignments ? "not-allowed" : "pointer"
              }}
              onClick={applyAdjustments}
              disabled={applyingAssignments}
            >
              {applyingAssignments ? "🔄 適用中..." : "✅ この配置を適用"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // 🆕 教育タイプに応じたバッジスタイル
  function getBadgeStyle(type) {
    switch (type) {
      case EDUCATION_TYPES.FIRST_OBSERVATION:
      case EDUCATION_TYPES.ADDITIONAL_OBSERVATION:
        return { backgroundColor: "#dbeafe", color: "#1e40af" };
      case EDUCATION_TYPES.GUIDED_PRACTICE:
        return { backgroundColor: "#dcfce7", color: "#166534" };
      case EDUCATION_TYPES.SUPERVISED_INDEPENDENCE:
        return { backgroundColor: "#fef3c7", color: "#92400e" };
      case EDUCATION_TYPES.FINAL_ASSESSMENT:
        return { backgroundColor: "#fce7f3", color: "#be185d" };
      case EDUCATION_TYPES.SURGICAL_ASSISTANCE:
        return { backgroundColor: "#e0f2fe", color: "#0c4a6e" };
      case EDUCATION_TYPES.SKILL_DEVELOPMENT:
        return { backgroundColor: "#f3e8ff", color: "#7c3aed" };
      case EDUCATION_TYPES.ADDITIONAL_TEACHING:
        return { backgroundColor: "#fef2f2", color: "#dc2626" };
      default:
        return { backgroundColor: "#f1f5f9", color: "#475569" };
    }
  }
};

export default AutoStaffAssignmentModal;